<div [formGroup]="form">
  <div class="page-header">
    <h1>Application on Behalf of {{header}}</h1>
  </div>

  <p>This section provides information regarding applying on behalf of an applicant.</p>
  @if (form.get('completingOnBehalfOf').value === originalOnBehalfOf) {
    <div class="attention">
      <p>You previously selected the option below. Verify that this is correct before continuing with the application.</p>
    </div>
  }

  <section class="radio-list">
    <app-field label="I am:" [required]="true" [valid]="isFieldValid('completingOnBehalfOf')" errorMessage="Please indicate who is completing this form.">
      <label>
        <input type="radio" [value]="100000000" name="completingOnBehalfOf" formControlName="completingOnBehalfOf">
        Completing this application for myself
      </label>
      <br />

      <label>
        <input type="radio" [value]="100000002" name="completingOnBehalfOf" formControlName="completingOnBehalfOf">
        A parent completing this application for my minor child (under 19 years of age)
      </label>
      <br />

      <label>
        <input type="radio" [value]="100000003" name="completingOnBehalfOf" formControlName="completingOnBehalfOf">
        A legal representative or legal guardian completing this application on behalf of someone else, or
        <br/>
        <div style="padding-left: 20px">a third party completing on behalf of an incapable adult.
          <app-tool-tip [trigger]="legalRepresentativeTooltip"></app-tool-tip>
        </div>
      </label>
    </app-field>
  </section>
  <ng-template #legalRepresentativeTooltip>If the person does not have a legal representative and is physically or mentally incapable of making the application, <strong>a third party</strong> may complete the application on their behalf. <strong>NOTE:</strong> This does not include victim service or other support workers who are providing assistance to a capable person.</ng-template>
  @if (form.get('completingOnBehalfOf').value === 100000003) {
    <div class="row">
      <div class="col-md-6">
        <app-field label="Relationship to Person" [required]="true" errorMessage="Please select a relationship" [valid]="isMyControlValid(form.get('relationshipToPerson'))">
          <select class="form-control" formControlName="relationshipToPerson">
            <option value="">Select...</option>
            @if (this.formType != ApplicationType.IFM_Application) {
              <span >
                @for (item of relationshipList; track item) {
                  <option [value]="item">{{ item }}</option>
                }
              </span>
            }
            @if (this.formType == ApplicationType.IFM_Application) {
              <span >
                @for (item of imfRelationshipList; track item) {
                  <option [value]="item">{{ item }}</option>
                }
              </span>
            }
          </select>
        </app-field>
      </div>
    </div>
  }

  <hr />

  @if (form.get('completingOnBehalfOf').value === 100000002 || form.get('completingOnBehalfOf').value === 100000003) {
    <section>
      <p>Complete the following before continuing with the application.</p>
      <h2 class="blue-header">Person Completing the Application</h2>
      <h3>Name</h3>
      <div class="row">
        <div class="col-md-4">
          <app-field label="First Name" [required]="true" [valid]="isFieldValid('representativeFirstName')" errorMessage="Please enter a first name">
            <input class="form-control" type="text" formControlName="representativeFirstName">
          </app-field>
        </div>
        <div class="col-md-4">
          <app-field label="Middle Name" [required]="false" errorMessage="Please enter a middle name">
            <input class="form-control" type="text" formControlName="representativeMiddleName">
          </app-field>
        </div>
        <div class="col-md-4">
          <app-field label="Last Name" [required]="true" [valid]="isFieldValid('representativeLastName')" errorMessage="Please enter a last name">
            <input class="form-control" type="text" formControlName="representativeLastName">
          </app-field>
        </div>
      </div>
      <h3>Contact Information</h3>

      <div class="row mt-3 mb-3">
        <div class="col-md-6">
          <app-field label="Preferred Method of Contact" [required]="true" [valid]="isFieldValid('representativePreferredMethodOfContact')" errorMessage="Please select your preferred method of contact" class="app-field-bold">
            <select class="form-control" formControlName="representativePreferredMethodOfContact">
              <option value="0">Select...</option>
              <option value="100000000">Email</option>
              <option value="100000001">Phone Call</option>
              <option value="100000002">Mail</option>
            </select>
          </app-field>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <app-field label="Are your email and phone number(s) the same as the applicants?" [valid]="isFieldValid('applicantSameContactInfo')" [required]="true" errorMessage="Please select whether your email and phone number(s) are the same">
            <label class="inline-label">
              <input type="radio" [value]="true" formControlName="applicantSameContactInfo">
              <span>Yes</span>
            </label>
            <label class="inline-label">
              <input type="radio" [value]="false" formControlName="applicantSameContactInfo">
              <span>No</span>
            </label>
          </app-field>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <app-field label="Phone Number (incl. area code)" [required]="representativePhoneIsRequired" [valid]="isFieldValid('representativePhoneNumber', form.get('applicantSameContactInfo').value)" errorMessage="Please enter a phone number">
            @if (form.get('representativeAddress.country').value === 'Canada' || form.get('representativeAddress.country').value === 'United States of America') {
              <input class="form-control" mask='(000) 000 0000' type="text" formControlName="representativePhoneNumber">
            } @else {
              <input class="form-control" type="text" formControlName="representativePhoneNumber" digitOnly>
            }
          </app-field>
        </div>
        <div class="col-md-6">
          <app-field label="Alternate Phone Number" [required]="false" [valid]="isFieldValid('representativeAlternatePhoneNumber', form.get('applicantSameContactInfo').value)" errorMessage="Please enter a phone number">
            @if (form.get('representativeAddress.country').value === 'Canada' || form.get('representativeAddress.country').value === 'United States of America') {
              <input class="form-control" mask='(000) 000 0000' type="text" formControlName="representativeAlternatePhoneNumber">
            } @else {
              <input class="form-control" type="text" formControlName="representativeAlternatePhoneNumber" digitOnly>
            }
          </app-field>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">          
          <app-form-field 
            label="Email Address" 
            [control]="form.get('representativeEmail')" 
            inputType="email"
            errorMessage="Please provide your email address">
          </app-form-field>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <app-field label="Confirm Email Address" [required]="representativeEmailIsRequired" [valid]="isFieldValid('representativeConfirmEmail', form.get('applicantSameContactInfo').value)" errorMessage="Please confirm your email address">
            <input class="form-control" type="text" (blur)="trimEmail(form.get('representativeConfirmEmail'))" formControlName="representativeConfirmEmail" maxlength="100">
          </app-field>
        </div>
      </div>
      <h3>Mailing Address</h3>
      <div class="row">
        <div class="col-12">
          <app-field label="Is your mailing address the same as the applicant's primary mailing address?" [valid]="isFieldValid('mostRecentMailingAddressSameAsPersonal')" [required]="true" errorMessage="Please make a selection" [showChevrons]="false">
            <label class="inline-label">
              <input type="radio" [value]="true" name="mostRecentMailingAddressSameAsPersonal" formControlName="mostRecentMailingAddressSameAsPersonal">
              <span>Yes</span>
            </label>
            <label class="inline-label">
              <input type="radio" [value]="false" name="mostRecentMailingAddressSameAsPersonal" formControlName="mostRecentMailingAddressSameAsPersonal">
              <span>No</span>
            </label>
          </app-field>
        </div>
      </div>
      <app-address (change)="setRepresentativePhoneValidators()" [group]="form.get('representativeAddress')" [showChildrenAsRequired]="true" [isDisabled]="form.get('mostRecentMailingAddressSameAsPersonal').value"></app-address>
      
      @if (form.get('completingOnBehalfOf').value === 100000003) {
        <h2 class="blue-header">Proof of Legal Authority, If Applicable</h2>
        <p>Note: The Crime Victim Assistance Program requires proof of your authority to apply for benefits on behalf of the applicant. Please attach the legal documentation here.
          <strong>File size limit:</strong> one file cannot exceed 2MB and all files uploaded to application cannot exceed 3.5MB
        </p>
        <app-file-uploader [formGroup]="form" [formType]="formType" [documents]="form.get('documents')"></app-file-uploader>
      }
    </section>
  }
</div>